<template>
  <div class="evidence-navigator">
    <ul>
      <li v-for="partMeta in partMetaData" :key="partNumberIndexMap[partMeta]">
        <div :id="`part-${partNumberIndexMap[partMeta]}`" class="part-title">
          {{ partTitle(partMeta) }}
        </div>
        <ul>
          <li
            v-for="(post, index) in getPostsByPart(partMeta)"
            :id="`part-${partNumberIndexMap[partMeta]}-task-${index + 1}`"
            :key="index"
            class="task-list list-item"
            :class="{
              selected: currentSelectedPostId === post.id,
              'not-passed': notPassedPosts.includes(post.id) && !finishedPosts.includes(post.id)
            }"
            @click="selectPost(post.id)"
          >
            <div class="selectable-item item">
              <div class="d-flex align-items-center">
                <div
                  class="radio-circle"
                  :class="{
                    checked: finishedPosts.includes(post.id)
                  }"
                ></div>
                <div
                  class="item-text text__sidebar-menu"
                  :class="{ selected: currentSelectedPostId === post.id }"
                >
                  {{ post.title }}
                </div>
              </div>
            </div>
          </li>
        </ul>
      </li>
    </ul>
    <div class="button-continue-container" :class="{ 'pt-0 pb-2': !isShowContinueButton }">
      <div v-if="isShowContinueButton">
        <!-- submission Quota Left -->
        <div class="text-quota-left">
          <div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 12.5L11.6667 14.1667L15 10.8334"
                stroke="#824224"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16.6665 6.66663H8.33317C7.4127 6.66663 6.6665 7.41282 6.6665 8.33329V16.6666C6.6665 17.5871 7.4127 18.3333 8.33317 18.3333H16.6665C17.587 18.3333 18.3332 17.5871 18.3332 16.6666V8.33329C18.3332 7.41282 17.587 6.66663 16.6665 6.66663Z"
                stroke="#824224"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3.33317 13.3333C2.4165 13.3333 1.6665 12.5833 1.6665 11.6666V3.33329C1.6665 2.41663 2.4165 1.66663 3.33317 1.66663H11.6665C12.5832 1.66663 13.3332 2.41663 13.3332 3.33329"
                stroke="#824224"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Submission Quota Left</span>
          </div>
          <div class="quota-count">
            <b>{{ countRemainingSubmission }}</b>
            /
            <b>{{ countTotalSubmission }}</b>
          </div>
        </div>
        <!-- Debug: {{ { isContinueable, isShowContinueButton } }} -->
        <div class="w-100 d-flex justify-content-center">
          <div class="button-wrapper">
            <b-button
              id="continue-button"
              class="w-100"
              :disabled="!isContinueable || isSubmitting"
              variant="success"
              @click="isContinueable ? onContinue() : null"
            >
              <b-spinner v-if="isSubmitting" small />
              <span v-else>Preview & Submit</span>
            </b-button>
          </div>
          <b-tooltip
            ref="continueTooltip"
            target="continue-button"
            placement="bottom"
            triggers=""
            variant="light"
            nowrap
            custom-class="single-line-tooltip"
          >
            The evidence can be continued once all sections are completed.
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Post from '@/models/Post';

// Constants
const PARTS = {
  OVERVIEW: 'Overview',
  WORK_EXAMPLE: 'Work Examples',
  REFLECTION: 'Reflection'
};

export default {
  name: 'EvidenceNavigator',
  props: {
    parts: {
      type: Object,
      required: true
    },
    partMetaData: {
      type: Array,
      required: true
    },
    finishedPosts: {
      type: Array,
      default: () => [27]
    },
    isContinueable: {
      type: Boolean,
      default: true
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    isShowContinueButton: {
      type: Boolean,
      default: true
    },
    selectedPostId: {
      type: Number,
      default: null
    },
    notPassedPosts: {
      type: Array,
      default: () => []
    },
    countRemainingSubmission: {
      type: Number,
      default: 0
    },
    countTotalSubmission: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectingOverride: null,
      isScrolling: false,
      entryList: [],
      observers: [],
      internalSelectedPostId: this.selectedPostId,
      partNumberIndexMap: {
        OVERVIEW: 1,
        WORK_EXAMPLE: 2,
        REFLECTION: 3
      }
    };
  },
  computed: {
    posts() {
      const { parts = {} } = this;
      const postIds = Object.values(parts).flat();
      return Post.query().whereIdIn(postIds).get();
    },
    selectedId() {
      if (!this.entryList.length) return null;

      const sortedEntries = this.getSortedEntries();
      const entryId = sortedEntries[0]?.target.id;

      return this.parseEntryIdToNavId(entryId);
    },
    currentSelectedPostId() {
      return this.internalSelectedPostId || this.selectedPostId;
    }
  },
  watch: {
    selectedId: {
      immediate: true,
      handler() {
        if (!this.isScrolling) {
          this.selectingOverride = null;
        }
      }
    },

    // Watch for external changes to selected post prop
    selectedPostId: {
      handler(newPostId) {
        if (newPostId !== this.internalSelectedPostId) {
          this.internalSelectedPostId = newPostId;
        }
      },
      immediate: true
    }
  },
  mounted() {
    const firstAvailablePart = this.partMetaData[0];

    // For initial selection, directly execute without checking for unsaved changes
    const initialPost = this.getPostsByPart(firstAvailablePart)[0];
    if (initialPost) {
      this.executePostSelection(initialPost.id);
    }
  },

  methods: {
    async onContinue() {
      //some validation or logic before continuing
      if (!this.isContinueable) {
        return;
      }
      this.$emit('continue');
    },
    showTooltipOnClick() {
      // Show tooltip programmatically when disabled button is clicked
      this.$refs.continueTooltip.$emit('open');
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.$refs.continueTooltip.$emit('close');
      }, 3000);
    },
    // Navigation methods

    selectPost(postId) {
      // Emit change-item event to let parent handle modal/save logic
      this.$emit('change-item', postId);
    },

    executePostSelection(postId) {
      this.internalSelectedPostId = postId;
      this.$emit('post-selected', postId);
    },

    // Method for parent to call to execute pending navigation
    executePendingNavigation(postId) {
      this.executePostSelection(postId);
    },

    // Method for parent to call to update selected post without triggering change-item
    updateSelectedPost(postId) {
      this.internalSelectedPostId = postId;
    },

    // Selection and display helpers
    getPostsByPart(part) {
      return this.posts.filter((post) => post.part === part);
    },

    partTitle(part) {
      return `Part ${this.partNumberIndexMap[part]} : ${PARTS[part] || part}`;
    },

    // Entry processing methods
    getSortedEntries() {
      return [...this.entryList].sort(this.compareEntries).concat(this.getDefaultEntries());
    },

    getDefaultEntries() {
      return [
        { target: { id: 'post-evaluation-assessment-submit' } },
        { target: { id: 'confirmation-evaluation-assessment' } }
      ];
    },

    compareEntries(a, b) {
      // Handle special entries
      if (this.isSpecialEntry(a.target.id)) return 1;
      if (this.isSpecialEntry(b.target.id)) return -1;

      const [partA, taskA] = this.parseCardId(a.target.id);
      const [partB, taskB] = this.parseCardId(b.target.id);

      // Handle null cases
      if (partA === null && partB !== null) return -1;
      if (partA !== null && partB === null) return 1;

      // Compare by part first, then by task
      if (partA !== partB) return partA - partB;
      return (taskA || 0) - (taskB || 0);
    },

    isSpecialEntry(id) {
      return (
        id === 'confirmation-evaluation-assessment' || id === 'post-evaluation-assessment-submit'
      );
    },

    parseCardId(id) {
      const match = id.match(/card__part?(\d+)-task-?(\d+)?/);
      if (match) {
        return match.slice(1).map(Number);
      }
      return [null, null];
    },

    parseEntryIdToNavId(entryId) {
      if (!entryId) return null;

      const idMatch = entryId.match(/card__part?(\d+)-task-?(\d+)?/);
      if (!idMatch) return entryId;

      const [part, task] = idMatch.slice(1).map(Number);
      return task ? `part-${part}-task-${task}` : `part-${part}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.part-title {
  font-size: 16px;
  font-weight: 600;
  padding: 12px 16px;
  color: #3b3e66;
  border-bottom: 1px solid #ddd;
}
.evidence-navigator {
  width: 100%;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 10.4px;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  font-weight: 500;
  color: #3c44b1;
  padding-top: 8px;
}

.evidence-navigator ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.evidence-navigator a {
  text-decoration: none;
  color: #333;
}

.evidence-navigator a:hover {
  text-decoration: underline;
}

.selectable-item {
  cursor: pointer;

  min-width: 0;
}

.part__work-example .selectable-item {
  padding-left: 0;
}

.selected {
  // background: linear-gradient(91.61deg, #667ce8 0%, #744ba3 100%);
  color: white;
}

.text-quota-left {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #824224;
  background-color: #f4772e1a;
  border: 1px solid #fcd9c4;
  border-radius: 8px;

  svg {
    margin-right: 8px;
  }

  .quota-count {
    font-size: 16px;
  }
}
.button-continue-container {
  width: 100%;
  padding: 16px;
}

.button-wrapper {
  position: relative;
  width: 100%;
}

.list-item {
  padding: 12px 16px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  color: #3c44b1;

  &:hover {
    background-color: #f8f9fa;
  }

  &.not-passed {
    background-color: #fff5f6;
  }

  &.selected {
    background: linear-gradient(91.61deg, #667ce8 0%, #744ba3 100%);
    color: white;

    .radio-circle {
      border-color: white;
      background-color: transparent;

      &.checked {
        border-color: white;
        background-color: white;

        &::after {
          display: none;
        }
      }
    }
  }

  .radio-circle {
    min-width: 16px;
    height: 16px;
    border: 1px solid #3c44b1;
    border-radius: 50%;
    margin-right: 12px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &.checked {
      border-color: #3c44b1;
      background-color: #3c44b1;

      &::after {
        display: none;
      }
    }
  }

  .item-text {
    font-size: 16px;
    font-weight: 600;

    &.selected {
      color: white;
    }
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media screen and (min-width: 992px) and (max-width: 1199px) {
  .evidence-navigator {
    max-width: 280px;
  }
}
</style>
